"use client";

import React, { useState, useEffect, useRef, useMemo } from "react";
import Image from "next/image";
import { Modal, Box, Typography, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { 
  Button, 
  Dropdown, 
  Input, 
  Label,
  Radio,
  Textarea,
  MultipleSelect
} from "@int-traderjoes/ui-components-library";
import { useTheme } from "@/context";
import { useLanguageContext } from "@/context/LanguageContext";
import AuthService from "@/services/authService";
import AllergensAndSensitivitiesService from "@/services/allergensAndSensitivitiesService";
import FileService from "@/services/fileService";
import { modalContainer, modalContent, modalHeader, modalInfo, modalStyles, attachmentLabelStyle, attachmentTooltipTriggerStyle } from "./sx";
import { decodeHtmlEntities, extractErrorMessage } from "@/utils/helper";
import ExpandableTooltip from "@/components/ExpandableTooltip";
import helpIcon from "@/assets/images/ideation-phase-images/HelpIcon.svg";
import { FILE_VALIDATION_CONFIG } from "@/utils/fileValidationConstants";
import useFileManagement from "@/hooks/useFileManagement";
import { showToast } from "@int-traderjoes/ui-components-library";
import { useLibraryComponents } from "@/hooks/useLibraryComponents";
import { useAllergenEditData } from "@/components/ActiveProjects/AllergensAndSensitivities/hooks";
import type {
  CreateOrUpdateAllergenRequest,
  CreateOrUpdateAllergenResponse
} from "@/components/ActiveProjects/AllergensAndSensitivities/types";
import type { AllergenData } from "@/hooks/useAllergens";
import Constants from "@/static/constants/constants.json";
import { handleApiError } from "@/hooks/useApiErrorHandler";

type OptionType = {
  label: string | React.ReactNode;
  value: string | number;
};

// Using shared AllergenData type from useAllergens

const DECLARED_YES = "Yes";
const DECLARED_NO = "No";
const LABEL_DECLARATION_NA = "N/A";

const RISK_PREFIX_B = "b.";
const RISK_PREFIX_C = "c.";
const RISK_PREFIX_D = "d.";
const RISK_PREFIX_E = "e.";

const RISK_LABEL_TOKEN_NOT_PRESENT = "not present";
const RISK_LABEL_TOKEN_PRODUCT_RECIPE = "product recipe";

const SPECIFY_PLACEHOLDER_NOT_APPLICABLE_ALLERGEN = "Not applicable for this allergen";
const SPECIFY_PLACEHOLDER_NOT_APPLICABLE_RISK = "Not applicable for this risk statement";

interface AllergenEditModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: AllergenData) => void;
  allergenData?: AllergenData;
  title?: string;
  errors?: string[];
  onFieldFocus?: (fieldName: string) => void;
  onFieldBlur?: (fieldName: string) => void;
  productId: string | number;
  variantId: string | number;
  siteAllergenTabId?: number | string;
  allergensIndicateThatApplyId?: string;
  ingredientId?: number;
  readOnly?: boolean;
}

const AllergenEditModal: React.FC<AllergenEditModalProps> = ({
  open,
  onClose,
  onSave,
  allergenData,
  title = "Edit Allergen",
  errors = [],
  onFieldFocus,
  onFieldBlur,
  productId,
  variantId,
  siteAllergenTabId,
  allergensIndicateThatApplyId,
  ingredientId,
  readOnly = false
}) => {
  const { darkMode } = useTheme();
  const { languageText } = useLanguageContext();
  const { FileUploadDisplay } = useLibraryComponents();
  
  // Theme class constant
  const themeClass = darkMode ? "dark" : "light";
  
  // Get labels from language context
  const allergenLabels = languageText?.TABLE_AND_FORM_HEADER?.TABLE_HEADER?.ALLERGEN_DATATABLE;
  const attachmentTooltipText = allergenLabels?.ATTACHMENTS_TOOLTIP;
  const buttonLabels = languageText?.BUTTONS;
  const placeholderLabels = languageText?.PLACEHOLDERS?.COMMON;
  const errorTexts = languageText?.MESSAGES?.VALIDATION_MESSAGES;
  const toastMessages = languageText?.MESSAGES?.TOAST_MESSAGES;
  
  // Get permissions for allergen form fields
  const permissions = AuthService.fetchPermissions("AllergenForm");

  // File management hook for allergen attachments
  const {
    uploadFile,
    downloadFile,
    removeFile,
    isUploading,
    hasFile,
    getFileName,
    getFileForPayload,
    clearAllFiles,
    populateExistingFiles
  } = useFileManagement({
    productId,
    variantId,
    documentFields: ['attachment'], // Single attachment field for allergen
    onFileStateChange: React.useCallback((fieldName: string, fileData: any) => {
      console.log(`🔄 [AllergenEditModal] File state changed for ${fieldName}:`, fileData);
      // Update form data when file upload completes
      setFormData(prev => ({
        ...prev,
        attachment: fileData ? {
          id: fileData.id,
          filename: fileData.filename,
          fileId: fileData.id,
          downloadUrl: fileData.downloadUrl,
          previewUrl: fileData.previewUrl
        } as any : null
      }));
    }, []),
    uploadOptions: {
      maxSize: 40 * 1024 * 1024, // 40MB
      allowedTypes: [],
      allowedExtensions: []
    }
  });

  // Form state
  const [formData, setFormData] = useState<AllergenData>({
    id: '',
    allergen: '',
    allergenDescription: '',
    riskStatement: '',
    formalControlProgram: 'Yes',
    declared: 'Yes',
    labelDeclaration: '',
    specify: '',
    source: '',
    attachment: null
  });

  // State for selected specify options (for multi-select)
  const [selectedSpecifyOptions, setSelectedSpecifyOptions] = useState<OptionType[]>([]);

  // State for saving
  const [isSaving, setIsSaving] = useState(false);

  // State for validation errors
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Use custom hook to fetch all dropdown options and allergen details
  const {
    dropdownOptions,
    allergenDetail,
    isLoadingOptions,
    isLoadingDetail
  } = useAllergenEditData({
    open,
    allergenData,
    errorTexts
  });

  // Destructure dropdown options
  const { riskStatementOptions, labelDeclarationOptions, specifyOptions } = dropdownOptions;

  // Determine if specify field should be disabled based on isSpecify field from API
  const isSpecifyFieldDisabled = useMemo(() => {
    // Get isSpecify from allergenData (from API) or formData (if already set)
    const isSpecify = allergenData?.isSpecify ?? formData.isSpecify;
    // isSpecify is "true" or "false" as string from API
    // Field should be disabled if isSpecify is "false" or not set
    return isSpecify !== "true";
  }, [allergenData?.isSpecify, formData?.isSpecify]);

  // Risk Statement rule: when option "B. Not present in Product Recipe, Ingredients, Production Equipment or Anywhere in Facility"
  // is selected, Specify must be non-editable and cleared.
  const selectedRiskStatementLabel = useMemo(() => {
    const selected = riskStatementOptions.find(
      (o) => String(o.value) === String(formData.riskStatement)
    );
    return String(selected?.label ?? "");
  }, [riskStatementOptions, formData.riskStatement]);

  const isRiskStatementNotPresentOption = useMemo(() => {
    const label = selectedRiskStatementLabel.trim().toLowerCase();
    if (!label) return false;
    // Be tolerant to punctuation/wording differences coming from API/localization.
    return (
      label.startsWith(RISK_PREFIX_B) &&
      label.includes(RISK_LABEL_TOKEN_NOT_PRESENT) &&
      label.includes(RISK_LABEL_TOKEN_PRODUCT_RECIPE)
    );
  }, [selectedRiskStatementLabel]);

  const isSpecifyDisabled = useMemo(
    () => isSpecifyFieldDisabled || isRiskStatementNotPresentOption,
    [isSpecifyFieldDisabled, isRiskStatementNotPresentOption]
  );

  // Specify mandatory override:
  // When risk statement is C/D/E and Declared is "No", Specify should be optional.
  const isRiskStatementCDE = useMemo(() => {
    const label = selectedRiskStatementLabel.trim().toLowerCase();
    if (!label) return false;
    return (
      label.startsWith(RISK_PREFIX_C) ||
      label.startsWith(RISK_PREFIX_D) ||
      label.startsWith(RISK_PREFIX_E)
    );
  }, [selectedRiskStatementLabel]);

  const isSpecifyRequired = useMemo(() => {
    const permissionRequires = Boolean(permissions?.specify?.isMandatory);
    if (isSpecifyDisabled) return false;
    if (String(formData.declared ?? "").trim().toLowerCase() === DECLARED_NO.toLowerCase() && isRiskStatementCDE) {
      return false;
    }
    return permissionRequires;
  }, [permissions?.specify?.isMandatory, isSpecifyDisabled, formData.declared, isRiskStatementCDE]);

  // Determine if source field should be mandatory based on risk statement
  // Source is mandatory only when "Present in Product Recipe" option is selected
  const isSourceMandatory = useMemo(() => {
    const riskStatementKeyword = allergenLabels?.RISK_STATEMENT_PRESENT_IN_RECIPE;
    return formData.riskStatement?.includes(riskStatementKeyword);
  }, [formData.riskStatement, allergenLabels?.RISK_STATEMENT_PRESENT_IN_RECIPE]);

  // Label Declaration: mandatory only when Declared = Yes; optional when Declared = No
  const isLabelDeclarationRequired = useMemo(
    () => formData.declared === DECLARED_YES,
    [formData.declared]
  );

  const naLabelDeclarationValue = useMemo(() => {
    const match = labelDeclarationOptions.find(
      (opt) =>
        String(opt.label).trim().toLowerCase() === LABEL_DECLARATION_NA.toLowerCase() ||
        String(opt.value).trim().toLowerCase() === LABEL_DECLARATION_NA.toLowerCase()
    );
    return match != null ? String(match.value) : LABEL_DECLARATION_NA;
  }, [labelDeclarationOptions]);

  const isLabelDeclarationDisabled = useMemo(
    () => formData.declared === DECLARED_NO,
    [formData.declared]
  );

  // When Declared = No, auto-select N/A once dropdown options are available
  useEffect(() => {
    if (!isLabelDeclarationDisabled || labelDeclarationOptions.length === 0) {
      return;
    }
    if (formData.labelDeclaration !== naLabelDeclarationValue) {
      setFormData((prev) => ({ ...prev, labelDeclaration: naLabelDeclarationValue }));
    }
  }, [
    isLabelDeclarationDisabled,
    labelDeclarationOptions.length,
    naLabelDeclarationValue,
    formData.labelDeclaration,
  ]);

  // Clear specify field when it becomes disabled and ensure it stays empty
  useEffect(() => {
    if (isSpecifyDisabled) {
      setSelectedSpecifyOptions([]);
      setFormData(prev => ({
        ...prev,
        specify: '' // Explicitly set to empty string for disabled field
      }));
    }
  }, [isSpecifyDisabled]);

  // Ensure specify field remains empty when disabled (additional safeguard)
  useEffect(() => {
    if (isSpecifyDisabled && formData.specify !== '') {
      setFormData(prev => ({
        ...prev,
        specify: ''
      }));
    }
  }, [isSpecifyDisabled, formData.specify]);

  // Note: fileInputKey is now handled internally by FileUploadDisplay component
  
  // Track if modal has been initialized to prevent form reset on allergenData changes
  const modalInitialized = useRef(false);
  // Prevent "deleted attachment reappears" due to async rehydration (race with detail fetch / init setTimeout)
  const deletedAttachmentRef = useRef(false);

  const yesNoOptions = [
    { label: DECLARED_YES, value: DECLARED_YES },
    { label: DECLARED_NO, value: DECLARED_NO }
  ];

  // Handle allergen detail response from custom hook
  useEffect(() => {
    if (!allergenDetail || !open) return;
    // If user deleted the attachment in this session, do not rehydrate it from async detail fetch.
    if (deletedAttachmentRef.current) {
      return;
    }

    // Update form data with attachments if present
    if (allergenDetail.attachments && allergenDetail.attachments.length > 0) {
      const firstAttachment = allergenDetail.attachments[0];
      if (!firstAttachment) {
        return;
      }
      
      // Check if document exists and has an ID (actual file uploaded)
      if (!firstAttachment.document || !firstAttachment.document.id) {
        // Clear any existing attachment from form data
        setFormData(prev => ({
          ...prev,
          attachment: null
        }));
        return;
      }
      
      const assetId = String(firstAttachment.document.id);
      
      const mappedAttachment = {
        id: assetId,
        filename: firstAttachment.documentTitle || 'Untitled Document',
        fileId: assetId,
        index: firstAttachment.index
      };

      // Update form data with attachment information
      setFormData(prev => ({
        ...prev,
        attachment: mappedAttachment
      }));

      // Populate file management hook with the attachment so it displays in FileUploadDisplay
      const existingFiles = {
        attachment: {
          id: assetId,
          filename: mappedAttachment.filename,
          uploadedAt: new Date().toISOString(),
          type: 'attachment',
          fullpath: `${productId}/${variantId}/allergens/${allergenData?.id}/attachment`,
          downloadUrl: '',
          previewUrl: ''
        }
      };

      populateExistingFiles(existingFiles);
    }
  }, [allergenDetail, open, productId, variantId, allergenData?.id, populateExistingFiles]);

  // Initialize form data when modal opens (only once per modal session)
  useEffect(() => {
    if (open && !modalInitialized.current) {
      // Mark modal as initialized
      modalInitialized.current = true;
      // New modal session: allow rehydration again
      deletedAttachmentRef.current = false;
      
      if (allergenData) {
        console.log('📋 [AllergenEditModal] Initializing form data with allergen data (first time):', allergenData);
        setFormData({ ...allergenData });
        
        // Initialize selectedSpecifyOptions from comma-separated specify values immediately
        if (allergenData.specify && allergenData.specify !== '') {
          console.log('📋 [AllergenEditModal] Initializing specify options immediately from:', allergenData.specify);
          const specifyValues = allergenData.specify.split(',').map(value => value.trim());
          // Only filter if specifyOptions is available (API data loaded)
          if (specifyOptions.length > 0) {
            const selectedOptions = specifyOptions.filter(option => 
              specifyValues.includes(String(option.value))
            );
            setSelectedSpecifyOptions(selectedOptions);
          } else {
            // Store the values to be processed when specifyOptions loads
            setSelectedSpecifyOptions([]);
          }
        } else {
          setSelectedSpecifyOptions([]);
        }
        
        // Populate existing file data if attachment exists
        // Use setTimeout to ensure useFileManagement hook is ready
        if (allergenData.attachment && (allergenData.attachment.id || allergenData.attachment.filename)) {
          console.log('📁 [AllergenEditModal] Populating existing attachment:', allergenData.attachment);
          
          setTimeout(() => {
            // If user deleted attachment while this timeout was pending, don't re-populate it.
            if (deletedAttachmentRef.current) {
              return;
            }
            console.log('📁 [AllergenEditModal] Current hasFile state before population:', hasFile('attachment'));
            
            const attachment = allergenData.attachment!; // We already checked it exists
            const existingFiles = {
              attachment: {
                id: attachment.id || attachment.fileId || '',
                filename: attachment.filename || '',
                fileId: attachment.fileId || attachment.id || '',
                downloadUrl: attachment.downloadUrl || '',
                previewUrl: attachment.previewUrl || '',
                fullpath: (attachment as any).fullpath || `${productId}/${variantId}/allergens/${allergenData.id}/attachment`,
                uploadedAt: (attachment as any).uploadedAt || new Date().toISOString(),
                type: 'attachment'
              }
            };
            
            console.log('📁 [AllergenEditModal] Populating files with data:', existingFiles);
            populateExistingFiles(existingFiles);
            
            // Check if population worked
            setTimeout(() => {
              console.log('📁 [AllergenEditModal] hasFile state after population:', hasFile('attachment'));
              console.log('📁 [AllergenEditModal] getFileName after population:', getFileName('attachment'));
            }, 50);
          }, 10);
        }
      } else {
        // Reset form for new allergen
        console.log('📋 [AllergenEditModal] Initializing form data for new allergen');
        setFormData({
          id: '',
          allergen: '',
          allergenDescription: '',
          riskStatement: '',
          formalControlProgram: 'Yes',
          declared: 'Yes',
          labelDeclaration: '',
          specify: '',
          source: '',
          attachment: null
        });
        setSelectedSpecifyOptions([]); // Reset selected specify options for new allergen
        clearAllFiles(); // Clear any existing files for new allergen
      }
    } else if (!open && modalInitialized.current) {
      // Reset initialization flag and clear files when modal closes
      modalInitialized.current = false;
      deletedAttachmentRef.current = false;
      setSelectedSpecifyOptions([]); // Reset selected specify options when modal closes
      clearAllFiles(); // Clear files when modal closes
    }
  }, [open, allergenData, productId, variantId, populateExistingFiles, clearAllFiles, specifyOptions, getFileName, hasFile]);

  // Monitor file state changes to ensure FileUploadDisplay updates
  useEffect(() => {
    if (open) {
      console.log('📁 [AllergenEditModal] File state monitoring:', {
        hasFile: hasFile('attachment'),
        fileName: getFileName('attachment'),
        formDataAttachment: formData.attachment
      });
    }
  }, [open, hasFile, getFileName, formData.attachment]);

  // Initialize selectedSpecifyOptions when formData.specify changes (after initial load)
  useEffect(() => {
    // Only run this if the modal is already initialized to avoid conflicts with initial load
    if (modalInitialized.current) {
      if (formData.specify && formData.specify !== '') {
        const specifyValues = formData.specify.split(',').map(value => value.trim());
        const selectedOptions = specifyOptions.filter(option => 
          specifyValues.includes(String(option.value))
        );
        setSelectedSpecifyOptions(selectedOptions);
      } else if (formData.specify === '') {
        setSelectedSpecifyOptions([]);
      }
    }
  }, [formData.specify, specifyOptions]);

  // Handle specify options initialization when API data loads after form initialization
  useEffect(() => {
    if (modalInitialized.current && specifyOptions.length > 0 && formData.specify && formData.specify !== '') {
      const specifyValues = formData.specify.split(',').map(value => value.trim());
      const selectedOptions = specifyOptions.filter(option => 
        specifyValues.includes(String(option.value))
      );
      setSelectedSpecifyOptions(selectedOptions);
    }
  }, [specifyOptions, formData.specify]);

  const handleInputChange = (field: keyof AllergenData, value: string) => {
    console.log('🔄 [AllergenEditModal] Input changed:', { field, value, modalInitialized: modalInitialized.current });
    setFormData(prev => {
      if (field === 'declared' && prev.declared !== value) {
        const labelDeclaration =
          value === DECLARED_NO
            ? naLabelDeclarationValue
            : value === DECLARED_YES
              ? ''
              : prev.labelDeclaration;
        return {
          ...prev,
          declared: value,
          labelDeclaration,
        };
      }
      return {
        ...prev,
        [field]: value
      };
    });
    
    // Clear validation error for this field when user starts typing
    if (validationErrors.includes(field)) {
      setValidationErrors(prev => prev.filter(error => error !== field));
    }
    // When Declared changes, labelDeclaration is updated — clear its error too.
    if (field === 'declared' && validationErrors.includes('labelDeclaration')) {
      setValidationErrors(prev => prev.filter(error => error !== 'labelDeclaration'));
    }
  };

  const handleDropdownChange = (field: keyof AllergenData) => 
    (e: React.ChangeEvent<HTMLInputElement>, option: any) => {
      setFormData(prev => ({
        ...prev,
        [field]: option.value
      }));
      
      // Clear validation error for this field when user makes selection
      if (validationErrors.includes(field)) {
        setValidationErrors(prev => prev.filter(error => error !== field));
      }
    };

  const handleSpecifyMultipleSelection = (selected: OptionType[]) => {
    // Don't allow changes if field is disabled
    if (isSpecifyDisabled) {
      return;
    }

    // Update the selected options state
    setSelectedSpecifyOptions(selected);

    // Prepare comma-separated values for form data
    const values = selected.map((option: OptionType) => option.value).join(",");

    // Update form data with the new values
    setFormData(prev => ({
      ...prev,
      specify: values
    }));
    
    // Clear validation error for specify field when user makes selection
    if (validationErrors.includes('specify')) {
      setValidationErrors(prev => prev.filter(error => error !== 'specify'));
    }
  };

  // File validation configuration for Allergens & Sensitivities (multiple types)
  const { allowedTypes: allergenAllowedTypes, toastMessage: allergenToastMessage } = FILE_VALIDATION_CONFIG.ALLERGENS_AND_SENSITIVITIES;

  const handleFileChange = async (file: File | null) => {
    if (!file) {
      // Handle file removal - Just remove from frontend, save API will handle deletion
      if (hasFile('attachment') || formData.attachment) {
        try {
          // Block any pending async rehydration from bringing the file back
          deletedAttachmentRef.current = true;
          // Step 1: Remove from file management hook (frontend only, no API call)
          if (hasFile('attachment')) {
            // File tracked by file management hook - use removeFile (frontend only)
            await removeFile('attachment');
          }
          
          // Step 2: Clear form data attachment
          setFormData(prev => ({
            ...prev,
            attachment: null
          }));
          
          // Step 3: Clear any validation errors related to attachment
          if (validationErrors.includes('attachment')) {
            setValidationErrors(prev => prev.filter(error => error !== 'attachment'));
          }
          
          showToast({
            type: "success",
            message: toastMessages?.ALLERGEN_FILE_REMOVE_SUCCESS || "Attachment removed successfully"
          });
        } catch (error) {
          console.error('❌ [AllergenEditModal] Error removing file:', error);
          handleApiError(
            error,
            toastMessages?.ALLERGEN_FILE_REMOVE_FAILED_ERROR || "Failed to remove attachment"
          );
        }
      }
      return;
    }

    try {
      // New upload overrides any prior delete in this session
      deletedAttachmentRef.current = false;
      console.log('🔄 [AllergenEditModal] Starting file upload for:', file.name);
      
      // Generate custom path with current form data
      const allergenId = allergenData?.id || formData.id || `temp_${Date.now()}`;
      const allergenName = (allergenData?.allergen || formData.allergen || 'unknown').replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
      const customPath = `${productId}/${variantId}/allergens/${allergenId}`;
      
      console.log('📁 [AllergenEditModal] Using custom path:', customPath);
      
      // Upload with dynamic custom path
      await uploadFile('attachment', file, customPath);
      console.log('✅ [AllergenEditModal] File upload successful');
      
      // Clear validation error for attachment when file is uploaded
      if (validationErrors.includes('attachment')) {
        setValidationErrors(prev => prev.filter(error => error !== 'attachment'));
      }
    } catch (error: any) {
      console.error('❌ [AllergenEditModal] File upload failed:', error);
      handleApiError(
        error,
        toastMessages?.ALLERGEN_FILE_UPLOAD_FAILED_ERROR || "Failed to upload attachment"
      );
    }
  };

  const handleSave = async () => {
    if (readOnly) {
      return;
    }
    setIsSaving(true);
    // Comprehensive validation based on permissions and field states
    const validationErrors: string[] = [];

    // Check allergen name (always required)
    if (!formData.allergen.trim()) {
      validationErrors.push("allergen");
      console.error("Please enter allergen name", {
        "allergen (allergen name)": formData.allergen,
      });
    }

    // Check risk statement (always required)
    if (!formData.riskStatement) {
      validationErrors.push("riskStatement");
      console.error("Please select a risk statement", {
        "riskStatement (risk statement)": formData.riskStatement,
      });
    }

    // Check formal control program if required
    if (permissions?.formalControlProgram?.isMandatory && !formData.formalControlProgram) {
      validationErrors.push("formalControlProgram");
      console.error("Please select formal control program", {
        "formalControlProgram (formal control program)": formData.formalControlProgram,
        "isMandatory": permissions?.formalControlProgram?.isMandatory,
      });
    }

    // Check declared if required
    if (permissions?.declared?.isMandatory && !formData.declared) {
      validationErrors.push("declared");
      console.error("Please select declared option", {
        "declared (declared option)": formData.declared,
        "isMandatory": permissions?.declared?.isMandatory,
      });
    }

    // Check label declaration: required only when Declared = Yes
    if (isLabelDeclarationRequired && !formData.labelDeclaration) {
      validationErrors.push("labelDeclaration");
      console.error("Please select label declaration", {
        "labelDeclaration (label declaration)": formData.labelDeclaration,
        "isLabelDeclarationRequired": isLabelDeclarationRequired,
      });
    }

    // Check specify field only if not disabled and required
    if (isSpecifyRequired && !formData.specify) {
      validationErrors.push("specify");
      console.error("Please select specify options", {
        "specify (specify options)": formData.specify,
        "isSpecifyRequired": isSpecifyRequired,
      });
    }

    // Check source if required (mandatory when risk statement is "A. Present in Product Recipe")
    if (isSourceMandatory && !formData?.source?.trim()) {
      validationErrors.push("source");
      console.error("Please enter source", {
        "source (source)": formData?.source,
        "isSourceMandatory": isSourceMandatory,
      });
    }

    // Check attachment if required
    if (permissions?.attachment?.isMandatory && !hasFile('attachment') && !formData.attachment) {
      validationErrors.push("attachment");
      console.error("Please upload attachment", {
        "attachment (attachment)": formData.attachment,
        "hasFile": hasFile('attachment'),
        "isMandatory": permissions?.attachment?.isMandatory,
      });
    }

    // If there are validation errors, show them and return
    if (validationErrors.length > 0) {
      console.error("Validation failed:", {
        "validationErrors (failed fields)": validationErrors,
      });
      setValidationErrors(validationErrors);
      setIsSaving(false); // Reset saving state on validation failure
      showToast({
        type: "error",
        message: errorTexts?.FIELD_VALIDATE_ERROR || "Please fill the mandatory fields"
      });
      return;
    }

    // Clear validation errors if validation passes
    setValidationErrors([]);

    try {
      // Get the file data for payload if attachment exists
      const attachmentPayload = getFileForPayload('attachment');
      
      console.log('💾 [AllergenEditModal] Building save payload:', {
        attachmentPayload,
        formDataAttachment: formData.attachment,
        hasFile: hasFile('attachment')
      });
      
      // Build attachments array for API payload
      const attachments = [];
      if (attachmentPayload) {
        // New file uploaded via file management hook
        attachments.push({
          index: 0,
          documentTitle: attachmentPayload.filename || "Allergen Document",
          document: {
            id: Number(attachmentPayload.id)
          }
        });
      } else if (formData.attachment && formData.attachment.id) {
        // Existing attachment from API detail (not newly uploaded)
        attachments.push({
          index: 0,
          documentTitle: formData.attachment.filename || "Allergen Document",
          document: {
            id: Number(formData.attachment.id)
          }
        });
      }
      // If both are null/empty, attachments will be an empty array [], indicating no attachment

      // Create API payload structure
      const apiPayload: CreateOrUpdateAllergenRequest = {
        // Don't include id for new entries (as per requirement)
        ...(allergenData?.allergenDeclarationId && { id: Number(allergenData.allergenDeclarationId) }),
        siteAllergenTabId: siteAllergenTabId || Number(productId),
        statementToBestDescribe: formData.riskStatement,
        formalControlProgram: formData.formalControlProgram,
        declared: formData.declared,
        labelDeclaration: formData.labelDeclaration,
        specify: isSpecifyDisabled ? '' : formData.specify,
        source: formData.source,
        allergensIndicateThatApplyId: allergensIndicateThatApplyId || formData.id,
        allergensIndicateThatApplyTitle: formData.allergen,
        attachments: attachments
      };

      console.log("🔄 [AllergenEditModal] Calling API with payload:", apiPayload);

      // Call the API
      const response: CreateOrUpdateAllergenResponse = await AllergensAndSensitivitiesService.saveForSpecificAllergenDetail(apiPayload);
      
      console.log("✅ [AllergenEditModal] API response:", response);

      // Show success toast
      showToast({
        type: "success",
        message: toastMessages?.ALLERGEN_SAVE_SUCCESS || "Allergen saved successfully"
      });

      // Create the final form data for parent component callback
      const finalFormData: AllergenData = {
        ...formData,
        allergenDeclarationId: response?.id || allergenData?.allergenDeclarationId,
        // Ensure specify field is empty string when disabled
        specify: isSpecifyDisabled ? '' : formData.specify,
        attachment: attachmentPayload ? {
          id: attachmentPayload.id,
          filename: attachmentPayload.filename,
          fileId: attachmentPayload.id,
          downloadUrl: formData.attachment?.downloadUrl,
          previewUrl: formData.attachment?.previewUrl
        } : null
      };

      // Call the parent callback with the form data
      onSave(finalFormData);
      onClose();
      
      console.log("✅ [AllergenEditModal] Allergen saved successfully");

    } catch (error: any) {
      console.error("❌ [AllergenEditModal] Error saving allergen:", error);
     handleApiError(
        error,
        toastMessages?.ALLERGEN_SAVE_ERROR || "Failed to save allergen"
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  // Helper function to check if field has error
  const hasFieldError = (fieldName: string): boolean => {
    return errors.includes(fieldName) || validationErrors.includes(fieldName);
  };

  // Handle field focus
  const handleFieldFocus = (fieldName: string) => {
    if (onFieldFocus) {
      onFieldFocus(fieldName);
    }
  };

  // Handle field blur
  const handleFieldBlur = (fieldName: string) => {
    if (onFieldBlur) {
      onFieldBlur(fieldName);
    }
  };

  return (
    <Modal 
      open={open} 
      onClose={onClose}
      sx={modalContainer(darkMode)}
    >
      <Box sx={modalStyles(darkMode)}>
        {/* Header */}
        <Box
          // display="flex"
          // justifyContent="space-between"
          // alignItems="center"
          // p={3}
          // borderBottom={darkMode ? "1px solid #444" : "1px solid #E0E0E0"}
          sx={modalHeader(darkMode)}
        >
          <Typography 
            variant="h6" 
            dangerouslySetInnerHTML={{ __html: title }}
          >
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon sx={{ color: darkMode ? "#FFFFFF" : "#000000" }} />
          </IconButton>
        </Box>

        {/* Content */}
        <Box 
          p={3} 
          flex="1"
          sx={modalContent(darkMode)}
        >
          {/* Note: Loading indicators are now handled by global loading context */}
          
          {/* Allergen Description */}
          {formData.allergenDescription && (
          <Box mb={2}>
            <Box 
              sx={modalInfo(darkMode)}
              
            >
              <Typography 
                variant="body2"
                sx={{ 
                  color: darkMode ? "#FFFFFF" : "#F57C00",
                  fontSize: "14px"
                }}
                dangerouslySetInnerHTML={{ __html: formData.allergenDescription }}
              >
              </Typography>
            </Box>
          </Box>
          )}
          {/* Risk Statement Dropdown */}
          <Box mb={3}>
            <Box mb={1}>
              <Label 
                text={allergenLabels?.CHOOSE_ONE_STATEMENT_TO_BEST_DESCRIBE_THE_HIGHEST_RISK_OF_PRESENCE_OF_ALLERGEN_IN_THE_PRODUCT}
                required={permissions?.riskStatement?.isMandatory || false}
                className={`${themeClass} ${hasFieldError('riskStatement') ? 'label-error-text' : ''}`}
              />
            </Box>
            <Dropdown
              className={`${themeClass} ${hasFieldError('riskStatement') ? 'field-error-color' : ''}`}
              options={riskStatementOptions}
              selectedValue={formData.riskStatement}
              onChange={handleDropdownChange('riskStatement')}
              placeholder={isLoadingOptions ? "Loading options..." : "Select risk statement"}
              name="riskStatement"
              onFocus={() => handleFieldFocus('riskStatement')}
              onBlur={() => handleFieldBlur('riskStatement')}
              disabled={isLoadingOptions || readOnly}
            />
          </Box>

          {/* Radio Button Row */}
          <Box display="flex" gap={3} mb={3}>
            {/* Formal Control Program */}
            <Box flex="1">
              <Box mb={1}>
                <Label 
                  text={allergenLabels?.FORMAL_CONTROL_PROGRAM}
                  required={permissions?.formalControlProgram?.isMandatory || false}
                  className={`${themeClass} ${hasFieldError('formalControlProgram') ? 'label-error-text' : ''}`}
                />
              </Box>
              <Radio
                options={yesNoOptions}
                name="formalControlProgram"
                onChange={(value) => handleInputChange('formalControlProgram', value)}
                selectedValue={formData.formalControlProgram}
                disabled={readOnly}
              />
            </Box>

            {/* Declared */}
            <Box flex="1">
              <Box mb={1}>
                <Label 
                  text={allergenLabels?.DECLARED}
                  required={permissions?.declared?.isMandatory || false}
                  className={`${themeClass} ${hasFieldError('declared') ? 'label-error-text' : ''}`}
                />
              </Box>
              <Radio
                options={yesNoOptions}
                name="declared"
                onChange={(value) => handleInputChange('declared', value)}
                selectedValue={formData.declared}
                disabled={readOnly}
              />
            </Box>
          </Box>

          {/* Dropdown Row */}
          <Box display="flex" gap={3} mb={3}>
            {/* Label Declaration - mandatory only when Declared = Yes */}
            <Box flex="1">
              <Box mb={1}>
                <Label 
                  text={allergenLabels?.LABEL_DECLARATION}
                  required={isLabelDeclarationRequired}
                  className={`${themeClass} ${hasFieldError('labelDeclaration') ? 'label-error-text' : ''}`}
                />
              </Box>
              <Dropdown
                className={`${themeClass} ${hasFieldError('labelDeclaration') ? 'field-error-color' : ''}`}
                options={labelDeclarationOptions}
                selectedValue={formData.labelDeclaration}
                onChange={handleDropdownChange('labelDeclaration')}
                placeholder={
                  isLabelDeclarationDisabled
                    ? LABEL_DECLARATION_NA
                    : isLoadingOptions
                      ? "Loading options..."
                      : "Select label declaration"
                }
                name="labelDeclaration"
                onFocus={() => handleFieldFocus('labelDeclaration')}
                onBlur={() => handleFieldBlur('labelDeclaration')}
                disabled={isLoadingOptions || readOnly || isLabelDeclarationDisabled}
              />
            </Box>

            {/* Specify */}
            <Box flex="1">
              <Box mb={1}>
                <Label 
                  text={allergenLabels?.SPECIFY}
                  required={isSpecifyRequired}
                  className={`${themeClass} ${hasFieldError('specify') ? 'label-error-text' : ''}`}
                />
              </Box>
              <MultipleSelect
                className={`${themeClass} ${hasFieldError('specify') ? 'field-error-color-multiple-select' : ''}`}
                options={specifyOptions}
                value={selectedSpecifyOptions}
                onChange={(value: OptionType[]) => handleSpecifyMultipleSelection(value)}
                disabled={isLoadingOptions || isSpecifyDisabled || readOnly}
                placeholder={
                  isSpecifyDisabled
                    ? (isRiskStatementNotPresentOption ? SPECIFY_PLACEHOLDER_NOT_APPLICABLE_RISK : SPECIFY_PLACEHOLDER_NOT_APPLICABLE_ALLERGEN)
                    : (isLoadingOptions ? "Loading options..." : "Select specify options")
                }
              />
            </Box>
          </Box>

          {/* Source */}
          <Box mb={3}>
            <Box mb={1}>
              <Label 
                text={allergenLabels?.SOURCE}
                required={isSourceMandatory}
                className={`${themeClass} ${hasFieldError('source') ? 'label-error-text' : ''}`}
              />
            </Box>
            <Textarea
              value={decodeHtmlEntities(formData.source)}
              onChange={(e) => handleInputChange('source', e.target.value)}
              placeholder="Enter source"
              className={`${hasFieldError('source') ? 'field-error-color' : ''}`}
              onFocus={() => handleFieldFocus('source')}
              onBlur={() => handleFieldBlur('source')}
              maxLength={Constants.MAX_LENGTH_3000}
                    disabled={readOnly}
            />
          </Box>

          {/* Attachment Section */}
          <Box mb={2}>
            <Box mb={1}>
              <Label
                text={allergenLabels?.ATTACHMENTS}
                htmlFor="allergen-attachment"
                required={permissions?.attachment?.isMandatory || false}
                className={`${themeClass} help-icon ${hasFieldError('attachment') ? 'label-error-text' : ''}`}
                style={attachmentLabelStyle}
              >
                <ExpandableTooltip text={attachmentTooltipText ?? ""} placement="bottom">
                  <span
                    style={attachmentTooltipTriggerStyle}
                    aria-label="attachment-tooltip"
                  >
                    <Image src={helpIcon} alt="Help" width={16} height={16} />
                  </span>
                </ExpandableTooltip>
              </Label>
            </Box>
            <Box width={"270px"}>
              {(() => {
                const hasFileData = hasFile('attachment') || !!formData.attachment;
                const fileName = getFileName('attachment') || formData.attachment?.filename || '';
                console.log('📁 [AllergenEditModal] FileUploadDisplay render state:', {
                  hasFile: hasFile('attachment'),
                  formDataAttachment: formData.attachment,
                  hasFileData,
                  fileName,
                  isUploading: isUploading('attachment'),
                  isSaving
                });
                
                return (
                  <FileUploadDisplay
                    hasUploadedFile={hasFileData}
                    fileName={fileName}
                    isUploading={isUploading('attachment') || isSaving}
                    uploadComplete={!isUploading('attachment') && Boolean(hasFile('attachment') || formData.attachment?.id)}
                    onFileChange={handleFileChange}
                    placeholder={placeholderLabels?.UPLOAD_DOCUMENT || "Upload Document"}
                    fileInputValue={null}
                    fileInputKey={`attachment-${formData.id || 'new'}-${hasFileData ? 'with-file' : 'no-file'}`}
                    allowedTypes={[...allergenAllowedTypes]}
                    toastMessage={allergenToastMessage}
                    onDownload={() => downloadFile('attachment')}
                    onDelete={readOnly ? () => {} : () => handleFileChange(null)}
                    darkModeClass={themeClass}
                    label="" // Label is already rendered above
                    htmlFor="allergen-attachment"
                    required={permissions?.attachment?.isMandatory || false}
                    disabled={readOnly}
                  />
                );
              })()}
            </Box>
          </Box>
        </Box>

        {/* Footer */}
        <Box
          display="flex"
          justifyContent="flex-end"
          gap={2}
          p={2}
          // borderTop={darkMode ? "1px solid #444" : "1px solid #E0E0E0"}
          flexShrink={0}
          // bgcolor={darkMode ? "#2D2D2D" : "#FFFFFF"}
        >
          <Button
            text={buttonLabels?.CANCEL_BUTTON}
            size="medium"
            primary={false}
            onClick={handleCancel}
            disabled={isSaving || isUploading('attachment')}
          />
          <Button
            text={buttonLabels?.SAVE}
            size="medium"
            primary={true}
            onClick={handleSave}
            disabled={isSaving || isUploading('attachment') || readOnly}
          />
        </Box>
      </Box>
    </Modal>
  );
};

export default AllergenEditModal;
